import axios from 'axios';
import type { AuthResponse, Category, InventoryItem, LoginCredentials, RegisterData, User, Product, Vendor, Sale, Purchase } from '@/types/api';

export const API_URL = import.meta.env.VITE_API_URL

// Create an axios instance with default config
const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  }
});

// Response interceptor for handling common errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle common errors
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      if (error.response.status === 400)
        return Promise.reject(error.response.data);
      else if (error.response.status === 401) {
        console.error('unauthorized');
      }
    } else if (error.request) {
      // The request was made but no response was received
      console.error('Network error, no response received', error);
    } else {
      // Something happened in setting up the request that triggered an Error
      console.error('Error setting up request:', error.message);
    }
    return Promise.reject(error);
  }
);

// API service functions
export const authService = {
  login: (credentials: LoginCredentials, LoginCredentials: any) => {
    return api.post<AuthResponse>('/auth/login', credentials);
  },
  register: (userData: RegisterData) => api.post<AuthResponse>('/auth/register', userData),
  logout: () => api.post('/auth/logout'),
  checkLogin: () => api.get<AuthResponse>('/auth/check')
};

export const inventoryService = {
  getItems: (params?: any) => api.get('/inventory', { params }),
  getItem: (id: number | string) => api.get(`/inventory/${id}`),
  createItem: (item: Partial<InventoryItem>) => api.post('/inventory', item),
  updateItem: (id: number | string, item: Partial<InventoryItem>) => api.put(`/inventory/${id}`, item),
  deleteItem: (id: number | string) => api.delete(`/inventory/${id}`),
};

export const dashboardService = {
  getSummary: () => api.get('/dashboard/summary'),
  getLowStocks: () => api.get('/dashboard/lowstocks'),
  getSlowMoving: () => api.get('/dashboard/slowmoving')
};

export const userService = {
  getItems: (params?: any) => api.get('/user', { params }).then(res => res.data),
  getItem: (id: number | string) => api.get(`/user/${id}`),
  createItem: (item: Partial<User>) => api.post('/user', item).then(res => res.data),
  updateItem: (item: Partial<User>) => api.put('/user/', item).then(res => res.data),
  deleteItem: (id: number | string) => api.delete(`/user/${id}`).then(res => res.data),
};

export const categoryService = {
  getItems: (params?: any) => api.get('/category', { params }).then(res => res.data),
  getItem: (id: number | string) => api.get(`/category/${id}`),
  createItem: (item: Partial<Category>) => api.post('/category', item).then(res => res.data),
  updateItem: (item: Partial<Category>) => api.put('/category/', item).then(res => res.data),
  deleteItem: (id: number | string) => api.delete(`/category/${id}`).then(res => res.data),
  getForSelect: (params?: any) => api.get('/category/ddl', { params }).then(res => res.data),
  upload: async (formData: FormData) => {
    const res = await api.post('/category/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return res.data;
  }
};

export const productService = {
  getItems: (params?: any) => api.get('/product', { params }).then(res => res.data),
  getItem: (id: number | string) => api.get(`/product/${id}`),
  createItem: (item: Partial<Product>) => api.post('/product', item).then(res => res.data),
  updateItem: (item: Partial<Product>) => api.put('/product', item).then(res => res.data),
  deleteItem: (id: number | string) => api.delete(`/product/${id}`).then(res => res.data),
  getForSelect: (id: number) => api.get(`/product/ddl/${id}`).then(res => res.data),
  upload: async (formData: FormData) => {
    const res = await api.post('/product/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return res.data;
  }
};

export const vendorService = {
  getItems: (params?: any) => api.get('/vendor', { params }).then(res => res.data),
  getItem: (id: number | string) => api.get(`/vendor/${id}`),
  createItem: (item: Partial<Vendor>) => api.post('/vendor', item).then(res => res.data),
  updateItem: (item: Partial<Vendor>) => api.put('/vendor/', item).then(res => res.data),
  deleteItem: (id: number | string) => api.delete(`/vendor/${id}`).then(res => res.data),
  getForSelect: (params?: any) => api.get('/vendor/ddl', { params }).then(res => res.data)
};

export const countryService = {
  getForSelect: (params?: any) => api.get('/country', { params }).then(res => res.data)
};

export const stateService = {
  getForSelect: (id: number | string) => api.get(`/state/${id}`).then(res => res.data)
};

export const purchaseService = {
  createItem: (item: Partial<Purchase>) => api.post('/purchase', item).then(res => res.data)
};

export const saleService = {
  getStocks: (params?: any) => api.get('/sale/stock', { params }).then(res => res.data),
  createItem: (item: Partial<Sale>) => api.post('/sale', item).then(res => res.data)
};

export const reportService = {
  getPurchases: (params?: any) => api.post('/report/purchase', params).then(res => res.data),
  getSales: (params?: any) => api.post('/report/sale', params).then(res => res.data),
  getStocks: (params?: any) => api.post('/report/stock', params).then(res => res.data),
  getProfits: (params?: any) => api.post('/report/profit', params).then(res => res.data)
};
export default api;